/**
 * GroupCategoryChipRow (iter85)
 *
 * Horizontal-scrollable chip row of Group Categories used at the top
 * of the Browse-by-Products and Browse-by-Shops pages.
 *
 * Previously these pages used to surface a "Verticals" rail (Food /
 * Pharmacy / Grocery) at the top, which is redundant — the customer
 * has already picked a vertical by tapping the home tile to land on
 * ``/c/:vertical`` or ``/c/:vertical/products``. The useful drill-down
 * inside a vertical is the tenant's own Group Categories ("Biryanis",
 * "Pizzas", …) so we swap the rail.
 *
 * Tapping a chip routes to ``/g/:slug`` (the unified Group feed) so
 * both browse modes flow into the same destination — the customer
 * sees products + shops contributing to that group on one page.
 *
 * Props:
 *   businessCategoryId  Optional vertical id to scope the chip list to
 *                       groups pinned to that vertical (or unpinned
 *                       globals). When omitted, returns every visible
 *                       group across the tenant.
 *   testIdPrefix        For unique data-testids per host page.
 *
 * Renders nothing when the backend returns zero visible groups (empty
 * tenants don't get a useless empty rail).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import { api } from "../lib/api";

export default function GroupCategoryChipRow({
    businessCategoryId,
    testIdPrefix = "group-chips",
}) {
    const [groups, setGroups] = useState(null);   // null = loading, [] = empty

    useEffect(() => {
        setGroups(null);
        const params = businessCategoryId ? { business_category_id: businessCategoryId } : {};
        api.get("/category-groups", { params })
            .then(({ data }) => setGroups(data || []))
            .catch(() => setGroups([]));
    }, [businessCategoryId]);

    // Render-time fast-paths.
    if (groups === null) {
        // Skeleton — keeps the layout height stable while the first
        // request resolves (prevents jumpy first paint on slow nets).
        return (
            <div className="px-4 mt-3 flex gap-2.5 overflow-x-auto no-scrollbar pb-1" data-testid={`${testIdPrefix}-loading`}>
                {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex-shrink-0 w-[68px] h-[68px] rounded-2xl bg-muted/40 animate-pulse" />
                ))}
            </div>
        );
    }
    if (groups.length === 0) return null;

    return (
        <nav
            data-testid={testIdPrefix}
            className="px-4 mt-3 flex gap-2.5 overflow-x-auto no-scrollbar pb-1"
        >
            {groups.map((g) => (
                <Link
                    key={g.id}
                    to={`/g/${g.slug}`}
                    data-testid={`${testIdPrefix}-${g.slug}`}
                    className="flex-shrink-0 w-[72px] flex flex-col items-center text-center active:scale-95 transition"
                >
                    <div className="w-[64px] h-[64px] rounded-2xl overflow-hidden bg-amber-100 flex items-center justify-center mb-1.5 shadow-sm border border-border">
                        {g.image_url ? (
                            <img src={g.image_url} alt={g.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                            <Package size={24} className="text-amber-700" />
                        )}
                    </div>
                    <div className="text-[11px] font-semibold leading-tight line-clamp-2">{g.name}</div>
                </Link>
            ))}
        </nav>
    );
}
